import { IconAlertOctagon, IconAlertTriangle, IconInfoCircle } from '@tabler/icons-react';
import type { Icon as TablerIcon } from '@tabler/icons-react';
import type { IssueNotice, IssueSeverity } from '@/types/issue-notice';

export interface SeverityMeta {
  Icon: TablerIcon;
  /** CSS variable names, so callers never hardcode colours. */
  fg: string;
  bg: string;
  rank: number;
}

/** UI-11: colour is always paired with an icon and a text label. */
export const SEVERITY_META: Record<IssueSeverity, SeverityMeta> = {
  critical: { Icon: IconAlertOctagon, fg: 'var(--severity-critical-fg)', bg: 'var(--severity-critical-bg)', rank: 0 },
  major: { Icon: IconAlertTriangle, fg: 'var(--severity-major-fg)', bg: 'var(--severity-major-bg)', rank: 1 },
  minor: { Icon: IconInfoCircle, fg: 'var(--severity-minor-fg)', bg: 'var(--severity-minor-bg)', rank: 2 },
  info: { Icon: IconInfoCircle, fg: 'var(--severity-info-fg)', bg: 'var(--severity-info-bg)', rank: 3 },
};

/** UI-3, mirrors the API so a stale cached list still sorts correctly. */
export function compareNotices(a: IssueNotice, b: IssueNotice): number {
  const bySeverity = SEVERITY_META[a.severity].rank - SEVERITY_META[b.severity].rank;
  if (bySeverity) return bySeverity;
  const scope = (n: IssueNotice) => (n.scopeType === 'platform' ? 0 : 1);
  if (scope(a) !== scope(b)) return scope(a) - scope(b);
  const endMs = (n: IssueNotice) => (n.endsAt ? Date.parse(n.endsAt) : Number.MAX_SAFE_INTEGER);
  const byEnd = endMs(a) - endMs(b);
  if (byEnd) return byEnd;
  return Date.parse(b.createdAt) - Date.parse(a.createdAt);
}

/** "All locations" or names truncated with "+N" (UI-4). */
export function scopeLabel(notice: Pick<IssueNotice, 'scopeType' | 'locations'>, allLabel: string, max = 2): string {
  if (notice.scopeType === 'platform') return allLabel;
  const names = notice.locations.map((l) => l.name);
  if (names.length <= max) return names.join(', ');
  return `${names.slice(0, max).join(', ')} +${names.length - max}`;
}

/** "Mon, Oct 26, 2:30 PM MDT" in the given zone (viewer's zone by default). */
export function formatNoticeTime(iso: string, withDate = true, timeZone?: string): string {
  const d = new Date(iso);
  return new Intl.DateTimeFormat('en-US', {
    ...(withDate ? { weekday: 'short', day: 'numeric', month: 'short' } : {}),
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZoneName: 'short',
    ...(timeZone ? { timeZone } : {}),
  }).format(d);
}

/** Remaining time as "4 min", "1 h 15 min", "2 d 3 h", or "45 s" under a minute. */
export function formatRemaining(ms: number): string {
  if (ms <= 0) return '0 min';
  const totalSeconds = Math.floor(ms / 1000);
  if (totalSeconds < 60) return `${totalSeconds} s`;
  const totalMinutes = Math.ceil(totalSeconds / 60);
  if (totalMinutes < 60) return `${totalMinutes} min`;
  const days = Math.floor(totalMinutes / 1440);
  const hours = Math.floor((totalMinutes % 1440) / 60);
  const minutes = totalMinutes % 60;
  if (days) return hours ? `${days} d ${hours} h` : `${days} d`;
  return minutes ? `${hours} h ${minutes} min` : `${hours} h`;
}

/** Wall-clock parts of an instant in a zone. */
function zonedParts(date: Date, timeZone: string) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).formatToParts(date);
  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value);
  return { y: get('year'), m: get('month'), d: get('day'), h: get('hour'), min: get('minute') };
}

/** ISO instant → datetime-local value ("2026-10-26T11:24") as seen in `timeZone`. */
export function toZonedInput(iso: string, timeZone: string): string {
  const p = zonedParts(new Date(iso), timeZone);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${p.y}-${pad(p.m)}-${pad(p.d)}T${pad(p.h)}:${pad(p.min)}`;
}

/** datetime-local value read as wall-clock time in `timeZone` → the real instant. */
export function fromZonedInput(value: string, timeZone: string): Date {
  const [datePart, timePart = '00:00'] = value.split('T');
  const [y, m, d] = datePart.split('-').map(Number);
  const [h, min] = timePart.split(':').map(Number);
  const asUtc = Date.UTC(y, m - 1, d, h, min);
  const offsetAt = (ms: number) => {
    const p = zonedParts(new Date(ms), timeZone);
    return Date.UTC(p.y, p.m - 1, p.d, p.h, p.min) - ms;
  };
  // Second pass corrects for a DST change between the guess and the answer.
  const guess = asUtc - offsetAt(asUtc);
  return new Date(asUtc - offsetAt(guess));
}

/** "Mountain Time (Denver)". */
export function zoneLabel(timeZone: string): string {
  if (timeZone === 'UTC') return 'UTC';
  let name = timeZone;
  try {
    name =
      new Intl.DateTimeFormat('en-US', { timeZone, timeZoneName: 'longGeneric' })
        .formatToParts(new Date())
        .find((p) => p.type === 'timeZoneName')?.value ?? timeZone;
  } catch {
    // Older engines without longGeneric fall back to the IANA id.
  }
  const city = timeZone.split('/').pop()?.replace(/_/g, ' ');
  return city && city !== name ? `${name} (${city})` : name;
}

/** Zones the form offers: the given ones first, then the usual US zones and UTC. */
export const COMMON_ZONES = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Phoenix',
  'America/Los_Angeles',
  'America/Anchorage',
  'Pacific/Honolulu',
  'UTC',
];

/** Zone a notice's times are shown in: the author's choice, else the locations' shared zone. */
export function noticeZone(notice: { timezone?: string | null; locations?: { timezone?: string | null }[] }): string | undefined {
  if (notice.timezone) return notice.timezone;
  const zones = new Set((notice.locations ?? []).map((l) => l.timezone).filter(Boolean) as string[]);
  return zones.size === 1 ? [...zones][0] : undefined;
}


export const isOtherCategory = (name?: string | null) => (name ?? '').trim().toLowerCase() === 'other';

/** "Other: Wi-Fi router" when the author described an Other issue, else the category name. */
export function categoryLabel(n: { categoryName: string; categoryOther?: string | null }): string {
  return isOtherCategory(n.categoryName) && n.categoryOther ? `Other: ${n.categoryOther}` : n.categoryName;
}

/** "11:24 AM" in the zone. */
export function formatClock(iso: string, timeZone?: string): string {
  return new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, ...(timeZone ? { timeZone } : {}) }).format(new Date(iso));
}

/** "Mon, 26 Oct" in the zone. */
export function formatDay(iso: string, timeZone?: string): string {
  return new Intl.DateTimeFormat('en-US', { weekday: 'short', day: 'numeric', month: 'short', ...(timeZone ? { timeZone } : {}) }).format(new Date(iso));
}

/** "Mountain Time", "India Standard Time" — the name people say, not an offset. */
export function zoneName(timeZone: string): string {
  if (timeZone === 'UTC') return 'UTC';
  for (const style of ['longGeneric', 'long'] as const) {
    try {
      const name = new Intl.DateTimeFormat('en-US', { timeZone, timeZoneName: style })
        .formatToParts(new Date())
        .find((p) => p.type === 'timeZoneName')?.value;
      if (name && !name.startsWith('GMT')) return name;
    } catch {
      // try the next style
    }
  }
  return timeZone.split('/').pop()?.replace(/_/g, ' ') ?? timeZone;
}
